
import { getFileUrl } from '@/clients/http'
import { Download, Eye, FolderTree, Pencil, RefreshCw, Trash2, Upload } from 'lucide-react'
import { FormProvider } from 'react-hook-form'

import { useProductsTable } from '@/hooks/features/products/useProductsTable'
import { useDataTable } from '@/hooks/ui'

import { AdvancedImage, Button } from '@/components/ui'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { ColumnVisibilityDropdown, DataTable, NoData } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination-controls'
import { Switch } from '@/components/ui/switch'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

import {
  ConfirmDialog,
  PageHeader,
  StatsCards,
  StatusBadge,
} from '@/components/atoms'

import { ProductModel } from '@/types/model/Product'

import { ProductViewDialog } from './ProductViewDialog'
import { ProductsFilters } from './ProductsFilters'

type TableColumns = 'image' | 'name' | 'category' | 'brand' | 'variation_count' | 'status' | 'actions'

const DEFAULT_VISIBLE_COLUMNS: TableColumns[] = [
  'image',
  'name',
  'category',
  'brand',
  'variation_count',
  'status',
  'actions',
]

export const ProductsTable = () => {
  const {
    loading,
    data,
    pagination,
    formMethods,
    stats,
    mode,
    productDialogOpen,
    viewProduct, // For potential view dialog
    isProductLoading,
    deleteDialogOpen,
    productToDelete,
    deleteLoading,
    exportLoading,
    importLoading,
    setPage,
    setPageSize,
    openCreatePage,
    exportProducts,
    handleFileSelect,
    fileInputRef,
    openViewDialog,
    openEditPage,
    closeDialog,
    handleDeleteClick,
    handleDeleteConfirm,
    setDeleteDialogOpen,
    toggleStatus,
    refresh
  } = useProductsTable()

  const { columns, changeColumnVisibility, disabledColumns } = useDataTable<
    ProductModel.Product,
    TableColumns
  >({
    columns: [
      {
        accessorKey: 'image',
        columnLabel: () => 'Image',
        cell: (data) => (
          <div className="flex items-center gap-2">
            <AdvancedImage
              src={getFileUrl(data?.thumbnail?.path)}
              alt={data?.name}
              width={40}
              height={40}
              className="size-10 rounded-md border border-border-subtle object-cover"
            />
          </div>
        ),
      },
      {
        accessorKey: 'name',
        columnLabel: () => 'Name',
        cell: (data) => <span className="font-medium text-ink">{data?.name}</span>,
        hasSorting: true,
      },
      {
        accessorKey: 'category',
        columnLabel: () => 'Category',
        cell: (data) => <span className="text-text-2">{data?.category?.name || 'N/A'}</span>,
        hasSorting: true,
      },
      {
        accessorKey: 'brand',
        columnLabel: () => 'Brand',
        cell: (data) => <span className="text-text-2">{data?.brand?.name || 'N/A'}</span>,
        hasSorting: true,
      },

      {
        accessorKey: 'variation_count',
        columnLabel: () => 'Variations',
        cell: (data) => <span className="tabular-nums text-text-2">{data?.variations_count}</span>,
        hasSorting: true,
      },

      {
        accessorKey: 'status',
        columnLabel: () => 'Status',
        cell: (data) => (
          <div className="flex items-center gap-2">
            <StatusBadge type="product" status={data?.status} />
            <Switch
              checked={data?.status === 'active'}
              onCheckedChange={() => toggleStatus(data)}
            />
          </div>
        ),
        hasSorting: true,
      },
      {
        accessorKey: 'actions',
        columnLabel: () => 'Actions',
        cell: (data) => (
          <div className="flex justify-end gap-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="View" onClick={() => openViewDialog(data)}>
                    <Eye className="text-muted-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Edit" onClick={() => openEditPage(data)}>
                    <Pencil className="text-muted-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Edit</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Delete" onClick={() => handleDeleteClick(data)}>
                    <Trash2 className="text-error" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Delete</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ),
        classes: () => ({
          column: 'text-right',
          cell: 'text-right',
        }),
        isAction: true,
      },
    ],
    visibleColumns: DEFAULT_VISIBLE_COLUMNS,
    disabledColumns: ['actions'],
  })

  // Computed stats from useProductsTable
  const productStats = stats

  return (
    <div className="space-y-4">
      <PageHeader
        title="Products"
        description="Manage your product catalog."
        onAddClick={openCreatePage}
        addButtonText="Add product"
        addButtonCopilotId="add_product_button"
        actions={
          <>
            <Button variant="secondary" onClick={exportProducts} disabled={exportLoading}>
              <Download />
              {exportLoading ? 'Exporting…' : 'Export'}
            </Button>
            <Button variant="secondary" onClick={() => fileInputRef.current?.click()} disabled={importLoading} data-copilot-id="import_products_button">
              <Upload />
              {importLoading ? 'Importing…' : 'Import'}
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".xlsx"
              className="hidden"
            />
          </>
        }
      />

      <StatsCards
        stats={[
          {
            title: 'Total products',
            value: productStats.totalProducts,
            icon: FolderTree,
          },
          {
            title: 'Active products',
            value: productStats.activeProducts,
          },
          {
            title: 'Inactive products',
            value: productStats.inactiveProducts,
          },
        ]}
      />

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col justify-between gap-3 lg:flex-row lg:items-center">
            <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
              <Pagination.PageSizeDropdown
                pageSize={pagination.pageSize}
                onPageSizeChange={setPageSize}
              />
              <FormProvider {...formMethods}>
                <ProductsFilters />
              </FormProvider>
            </div>

            <div className="order-first flex items-center justify-end gap-2 lg:order-last">
              <Button variant="outline" size="icon" onClick={refresh} disabled={loading} aria-label="Refresh">
                <RefreshCw className={loading ? 'animate-spin' : undefined} />
              </Button>
              <ColumnVisibilityDropdown
                columns={columns}
                onColumnVisibilityChange={changeColumnVisibility}
                disabledColumns={disabledColumns}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!loading && data.length === 0 ? (
            <NoData
              title="No products found"
              btnText="Add product"
              openCreateDialog={openCreatePage}
            />
          ) : (
            <>
              <DataTable columns={columns} data={data} loading={loading} />

              <Pagination.Buttons
                currentPage={pagination.currentPage}
                totalItems={pagination.totalCount}
                pageSize={pagination.pageSize}
                onPageChange={setPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Are you sure?"
        description={`This will permanently delete the product "${productToDelete?.name}". This action cannot be undone.`}
        confirmText={deleteLoading ? 'Deleting...' : 'Delete'}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
        variant="destructive"
      />

      <ProductViewDialog
        open={productDialogOpen}
        onOpenChange={(open) => !open && closeDialog()}
        product={viewProduct}
        isLoading={isProductLoading}
        onEdit={(product) => {
          closeDialog()
          openEditPage(product)
        }}
        onDelete={(product) => {
          closeDialog()
          handleDeleteClick(product)
        }}
      />
    </div>
  )
}
